import AppIcon from "../components/AppIcon";
import { useEffect, useState } from "react";
import { IMAGES } from "../constants/images";
import Sidebar from "../components/Sidebar";
import { useUser } from "../contexts/UserContext";
import useMoveTo from "../constants/navigation";
import { LuPencil as FaEdit } from "react-icons/lu";
import { ROUTES } from "../App";
import { database } from "../constants/firebase";
import { ref, onValue } from "firebase/database";
import { useParams } from "react-router";
import MobileHeader from "../components/MobileHeader";

const ManageChapter = () => {
  const navigate = useMoveTo();
  const { currentUser } = useUser();
  const { courseId } = useParams();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadedImages, setLoadedImages] = useState({}); // Track image load per chapter

  useEffect(() => {
    if (!currentUser || !courseId) return;

    const chaptersRef = ref(database, `Listings/${courseId}/chapters`);
    const unsubscribe = onValue(chaptersRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const chaptersArray = Object.keys(data).map((key) => ({
          id: key,
          ...data[key],
        }));
        setListings(chaptersArray);
      } else {
        setListings([]);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [currentUser, courseId]);

  const handleImageLoad = (id) => {
    setLoadedImages((prev) => ({ ...prev, [id]: true }));
  };

  // Skeleton loader for chapters
  const ChapterSkeleton = () => (
    <div className="space-y-4 w-full">
      {[...Array(4)].map((_, i) => (
        <div
          key={i}
          className="flex items-center gap-4 border-t border-gray-200 dark:border-[#D9D9D9]/20 px-4 py-4 animate-pulse"
        >
          <div className="w-16 h-16 md:w-24 md:h-24 rounded-lg bg-gray-300 dark:bg-gray-700"></div>
          <div className="flex-1 space-y-2">
            <div className="h-4 bg-gray-300 dark:bg-gray-700 w-20"></div>
            <div className="h-3 bg-gray-300 dark:bg-gray-700 w-40"></div>
          </div>
          <div className="w-20 h-8 bg-gray-300 dark:bg-gray-700 rounded"></div>
        </div>
      ))}
    </div>
  );

  return (
    <div className="h-screen bg-white text-black dark:bg-[#23272c] dark:text-white overflow-y-auto">
      <MobileHeader title="Manage Chapters" isBackBtn={true} />
      <div className="flex">
        <Sidebar />
        <main className="flex-1 h-screen pt-5 pb-24 md:pb-20 px-6 overflow-y-auto relative">
          {/* Top Header */}
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate(-1)}
              className="flex-shrink-0 md:block hidden"
            >
              <AppIcon
                name="arrowBack"
                className="w-4 h-4 md:w-5 md:h-5 grayscale brightness-0 dark:invert"
                alt="Back"
              />
            </button>
            <h2 className="text-2xl md:text-3xl font-semibold hidden md:block">
              Manage Chapters
            </h2>
            <div />
          </div>

          {/* Scrollable List */}
          <div className="flex-1 overflow-y-auto px-2 md:py-8 py-3 flex md:mt-16 mt-2">
            <div className="w-full flex flex-col justify-center items-center min-h-full">
              {loading ? (
                <ChapterSkeleton />
              ) : listings.length === 0 ? (
                <p className="text-center text-gray-500 dark:text-gray-400 py-8">
                  No chapters found.
                </p>
              ) : (
                <div className="w-full space-y-4">
                  {listings.map((item) => {
                    const isLoaded = loadedImages[item.id];
                    return (
                      <div
                        key={item.id}
                        className="flex items-center justify-between border-t text-black dark:border-[#D9D9D9]/20 border-gray-200 dark:text-white px-4 py-4"
                      >
                        <div className="flex gap-4 md:max-w-[40%] max-w-full">
                          {/* Image with shimmer */}
                          <div className="relative w-16 h-16 md:w-24 md:h-24 flex-shrink-0">
                            {!isLoaded && (
                              <div className="absolute inset-0 bg-gray-300 dark:bg-gray-700 rounded-lg animate-pulse"></div>
                            )}
                            <img
                              src={item.coverImageUrl || IMAGES.courses}
                              alt="Chapter"
                              className={`w-16 h-16 md:w-24 md:h-24 rounded-lg object-cover transition-opacity duration-300 ${isLoaded ? "opacity-100" : "opacity-0"}`}
                              onLoad={() => handleImageLoad(item.id)}
                              onError={() => handleImageLoad(item.id)}
                            />
                          </div>
                          <div>
                            <div className="font-bold text-lg line-clamp-1">
                              {item.title}
                            </div>
                            <div className="text-sm text-gray-700 dark:text-white line-clamp-3 min-w-full">
                              {item.description || "No description provided"}
                            </div>
                          </div>
                        </div>

                        {/* Buttons */}
                        <div className="flex flex-col items-end gap-4 text-sm">
                          <button
                            className="flex items-center gap-2 text-green-500 hover:cursor-pointer bg-gray-200 dark:bg-gray-500/30 px-2 py-1 rounded-md text-xs md:text-sm hover:bg-green-100 dark:hover:bg-green-900/30"
                            onClick={() =>
                              navigate(ROUTES.ADD_CHAPTER, {
                                state: { chapter: item, courseId },
                              })
                            }
                          >
                            <FaEdit className="text-green-500 size-3 md:size-3" />
                            Chapter Info
                          </button>
                          <button
                            className="flex items-center gap-2 text-orange-500 hover:cursor-pointer bg-gray-200 dark:bg-gray-500/30 px-2 py-1 rounded-md text-xs md:text-sm hover:bg-orange-100 dark:hover:bg-orange-900/30"
                            onClick={() =>
                              navigate(
                                `${ROUTES.CHAPTER_CONTENT}/${courseId}`,
                                { state: { chapterId: item.id } },
                              )
                            }
                          >
                            <FaEdit className="text-orange-500 size-3 md:size-3" />
                            Edit Content
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Fixed Add Chapter Button */}
          <div className="fixed bottom-24 md:bottom-6 left-1/2 -translate-x-1/2 w-[92%] max-w-3xl">
            <button
              type="button"
              onClick={() =>
                navigate(ROUTES.ADD_CHAPTER, {
                  state: { courseId: courseId || null },
                })
              }
              className="w-full bg-[#327691] hover:bg-[#2a647a] transition-colors text-white font-semibold rounded-2xl md:py-5 py-3 text-lg md:text-xl"
            >
              Add Chapter
            </button>
          </div>
        </main>
      </div>
    </div>
  );
};

export default ManageChapter;
